import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import isEqual from "lodash/isEqual";
import * as SavedReportConstants from "../../../constants/SavedReportConstants";

// Opens the saved report named by ?saved=<id> on a report screen.
// Loads it, calls onApply(savedReport) once per fresh record when it
// arrives, and after a create THIS screen started points the URL at the new
// report (without applying it again: the screen already shows its filters).
// Returns the open saved report, or null.
//
// The whole redux store is persisted to localStorage (configureStore.js) and
// rehydrated on every mount, so savedReports.action/record/requesting can
// already be settled ("create" or "details") before this mount has asked for
// anything:
// - "create" is only redirected to when createdRef.current is true, which is
//   set only by the screen's own create wrapper right before it dispatches
//   (see Report.js). A "create" left over from an earlier visit (possibly of
//   another kind) is ignored instead of rewriting the URL and silently
//   becoming the open report (which would then let "Save" overwrite it with
//   whatever default filters the screen happens to show).
// - "details" is applied once per distinct record object rather than once
//   per id. A record already settled in the persisted store for the id in
//   the URL is still shown immediately (as before this fix), but the fresh
//   load this mount always dispatches for that id still gets a chance to
//   apply its own answer if it turns out to differ, instead of being
//   permanently blocked by the first (possibly stale) application.
//   A fresh copy whose level and filters equal the copy already applied is
//   not applied again: the admin may have changed a filter while it loaded
//   (B-BUG-3), and applying the same filters again would put those back and
//   reset paging for nothing. A fresh copy that differs (the report was
//   changed elsewhere) is applied: it is what Save would otherwise overwrite.

// Whether two copies of a saved report would put the same filters on screen.
const sameFilters = (applied, fresh) =>
  applied?.id === fresh?.id &&
  applied?.kind === fresh?.kind &&
  applied?.level === fresh?.level &&
  isEqual(applied?.filters, fresh?.filters);

const useOpenSavedReport = ({
  kind,
  savedReport,
  savedReportAction,
  savedReportRequesting,
  savedReportError,
  loadSavedReport,
  createdRef,
  onApply,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const savedReportId = searchParams.get(
    SavedReportConstants.SAVED_REPORT_QUERY_PARAM
  );
  const requestedIdRef = useRef(null);
  const appliedRecordRef = useRef(null);
  const applyRef = useRef(onApply);
  applyRef.current = onApply;

  useEffect(() => {
    if (savedReportId && requestedIdRef.current !== savedReportId) {
      requestedIdRef.current = savedReportId;
      loadSavedReport({ id: savedReportId });
    }
  }, [savedReportId, loadSavedReport]);

  useEffect(() => {
    if (
      savedReportAction === "details" &&
      !savedReportRequesting &&
      savedReport?.id === savedReportId &&
      savedReport?.kind === kind &&
      appliedRecordRef.current !== savedReport
    ) {
      const applied = appliedRecordRef.current;
      appliedRecordRef.current = savedReport;
      if (!sameFilters(applied, savedReport)) {
        applyRef.current(savedReport);
      }
    }
  }, [
    savedReportAction,
    savedReportRequesting,
    savedReport,
    savedReportId,
    kind,
  ]);

  useEffect(() => {
    if (
      savedReportAction === "create" &&
      !savedReportRequesting &&
      createdRef?.current
    ) {
      createdRef.current = false;
      if (
        !savedReportError &&
        savedReport?.id &&
        savedReport?.kind === kind &&
        savedReport.id !== savedReportId
      ) {
        // The create response already IS this record: mark its id as
        // requested and the record itself as applied, so the URL change
        // below does not also trigger a redundant loadSavedReport, nor a
        // second onApply (which would reset paging and could clobber edits
        // made between the create succeeding and that reload answering).
        requestedIdRef.current = savedReport.id;
        appliedRecordRef.current = savedReport;
        setSearchParams({
          [SavedReportConstants.SAVED_REPORT_QUERY_PARAM]: savedReport.id,
        });
      }
    }
  }, [
    savedReportAction,
    savedReportRequesting,
    savedReportError,
    savedReport,
    savedReportId,
    kind,
    createdRef,
    setSearchParams,
  ]);

  return savedReportId &&
    savedReport?.id === savedReportId &&
    savedReport?.kind === kind
    ? savedReport
    : null;
};

export default useOpenSavedReport;
